import { useMemo, useEffect } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import Helmet from '../lib/Helmet';
import { BUSINESS } from '../data/pricing';
import { formatINR } from '../lib/quote';
import { deliveryDateFor } from '../lib/deliveryDate';

// Route: /order-confirmed?ref=CC-...
//
// Its own page, so a refresh or a back-and-forth never loses the
// confirmation. The order is passed from checkout and also kept on the
// device, so it survives a reload. The same details go out by email from the
// server, which is the record that matters.

const LAST_ORDER_KEY = 'cc_last_order';

// Google Customer Reviews. After a paid order Google asks the buyer, in its
// own small tray, whether they will rate the purchase, then emails the survey
// after the estimated delivery date. Ratings build the seller rating that
// shows as stars on Shopping and Search ads. Merchant Center must be enrolled
// in the Customer Reviews programme for the tray to appear.
const GCR_MERCHANT_ID = 5845704332;

// Google surveys after this date, so leave a few days for the call that fixes
// the slot, and for delivery itself.
function surveyDate(paidAt) {
  const base = paidAt ? new Date(paidAt) : new Date();
  const d = deliveryDateFor(Number.isNaN(base.getTime()) ? new Date() : base);
  d.setDate(d.getDate() + 3);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function readOrder(ref, state) {
  if (state?.order && (!ref || state.order.ref === ref)) return state.order;
  try {
    const o = JSON.parse(localStorage.getItem(LAST_ORDER_KEY) || 'null');
    if (o && (!ref || o.ref === ref)) return o;
  } catch { /* storage off */ }
  return null;
}

function Step({ n, title, children, done, last }) {
  return (
    <li className="relative flex gap-3 pb-4">
      {!last && <span className="absolute left-[13px] top-8 bottom-0 w-0.5 bg-gray-200" aria-hidden="true" />}
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-extrabold ${done ? 'bg-green-700 text-white' : 'bg-blue-50 text-blue-700'}`}>
        {done
          ? <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          : n}
      </span>
      <div className="min-w-0">
        <p className="text-[15px] font-bold text-gray-900">{title}</p>
        <p className="mt-0.5 text-[13px] leading-relaxed text-gray-600">{children}</p>
      </div>
    </li>
  );
}

export default function OrderConfirmed() {
  const [params] = useSearchParams();
  const { state } = useLocation();
  const ref = params.get('ref') || '';
  const order = useMemo(() => readOrder(ref, state), [ref, state]);

  // Ask once per order, and only when we have the buyer's email.
  useEffect(() => {
    if (!order?.ref || !order?.email) return;
    const flag = `cc_gcr_${order.ref}`;
    try {
      if (sessionStorage.getItem(flag)) return;
      sessionStorage.setItem(flag, '1');
    } catch { /* storage off: may ask again on a reload, which is harmless */ }
    window.renderOptIn = () => {
      window.gapi.load('surveyoptin', () => {
        window.gapi.surveyoptin.render({
          merchant_id: GCR_MERCHANT_ID,
          order_id: order.ref,
          email: order.email,
          delivery_country: 'IN',
          estimated_delivery_date: surveyDate(order.paidAt),
          opt_in_style: 'BOTTOM_TRAY',
        });
      });
    };
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/platform.js?onload=renderOptIn';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, [order]);

  const whatsapp = `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(`Hi, about my order ${order?.ref || ref}`)}`;

  if (!order) {
    return (
      <main className="mx-auto max-w-xl px-4 pb-16 pt-28 text-center">
        <Helmet>
          <title>Order confirmed | Citi Cool</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-xl font-bold text-gray-900">Thank you for your order</h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          {ref ? <>Your order <span className="font-mono font-semibold">{ref}</span> is confirmed. </> : null}
          The full details are in your confirmation email. For anything else, WhatsApp or call us.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <a href={whatsapp} target="_blank" rel="noopener noreferrer"
            className="rounded-xl border-[1.5px] border-green-600 px-5 py-3 text-sm font-bold text-green-700">WhatsApp us</a>
          <a href={`tel:${BUSINESS.phone}`} className="rounded-xl border-[1.5px] border-gray-300 px-5 py-3 text-sm font-bold text-gray-900">Call us</a>
        </div>
      </main>
    );
  }

  const first = (order.name || '').split(' ')[0];
  const booking = order.plan === 'booking';
  const steps = booking ? [
    { title: 'Booking confirmed', body: `Your unit is reserved for ${order.holdDays || 3} days. A confirmation is on its way to ${order.email}.`, done: true },
    { title: 'Photo of your unit', body: `We WhatsApp a photo of your actual unit to +91 ${order.mobile}.` },
    { title: 'Pay the balance', body: `We send you a link to pay the balance of ${formatINR(order.balance)} before dispatch.` },
  ] : [
    { title: 'Order confirmed', body: `A confirmation is on its way to ${order.email}.`, done: true },
  ];
  steps.push(
    { title: 'We call you to fix the time', body: `On +91 ${order.mobile}, for delivery ${booking ? 'once the balance is paid' : order.prefDay ? `on ${order.prefDay}` : `from ${order.deliveryBy}`}.` },
    {
      title: order.exchangeTotal > 0 ? 'Delivery and old AC pickup' : 'Delivery',
      body: `${order.units} unit${order.units === 1 ? '' : 's'} delivered to your door.${order.exchangeTotal > 0 ? ' Your old AC is uninstalled and taken away, free.' : ''}`,
    },
  );
  if (order.install) {
    steps.push({ title: 'Installation survey', body: 'Our engineer visits, measures the pipe runs and quotes the fitting.' });
  }

  return (
    <>
      <Helmet>
        <title>{`${booking ? 'Booking' : 'Order'} ${order.ref} confirmed | Citi Cool`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <main className="mx-auto w-full max-w-xl space-y-3 px-4 pb-16 pt-24 lg:pt-28">
        <section className="rounded-2xl border border-gray-200 bg-white p-5 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg viewBox="0 0 24 24" className="h-9 w-9 text-green-700" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
          </span>
          <h1 className="mt-3 text-2xl font-extrabold text-gray-900">Thank you{first ? `, ${first}` : ''}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {booking ? `${formatINR(order.paidNow)} booking paid · balance ${formatINR(order.balance)} before dispatch` : `${formatINR(order.paidNow ?? order.total)} paid online`}
          </p>
          <p className="mt-3 inline-block rounded-lg bg-gray-100 px-3 py-1.5 font-mono text-base font-bold text-gray-900">Order {order.ref}</p>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4">
          <h2 className="mb-3 text-base font-bold text-gray-900">What happens next</h2>
          <ol>
            {steps.map((s, i) => (
              <Step key={s.title} n={i + 1} title={s.title} done={s.done} last={i === steps.length - 1}>{s.body}</Step>
            ))}
          </ol>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-base font-bold text-gray-900">Delivering to</h2>
          <div className="text-sm leading-relaxed text-gray-900">
            <p>{order.name}</p>
            {(order.address || []).map((line) => <p key={line}>{line}</p>)}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4">
          <dl className="text-sm">
            {(order.lines || []).map((l) => (
              <div key={`${l.idu}-${l.kit}`} className="flex justify-between gap-3 py-1 text-gray-800">
                <dt className="min-w-0">{l.title}{l.qty > 1 ? ` × ${l.qty}` : ''}</dt>
                <dd className="shrink-0 tabular-nums">{formatINR(l.equipment)}</dd>
              </div>
            ))}
            <div className="flex justify-between py-1 text-gray-800">
              <dt>Delivery</dt><dd className="tabular-nums">{order.deliveryFree ? 'Free' : formatINR(order.deliveryAmount)}</dd>
            </div>
            {order.exchangeTotal > 0 && (
              <div className="flex justify-between py-1 font-medium text-green-700">
                <dt>Old AC exchange credit</dt><dd className="tabular-nums">&minus; {formatINR(order.exchangeTotal)}</dd>
              </div>
            )}
            {order.offer && order.offer.amount > 0 && (
              <div className="flex justify-between py-1 font-medium text-green-700">
                <dt>Offer {order.offer.code}</dt><dd className="tabular-nums">&minus; {formatINR(order.offer.amount)}</dd>
              </div>
            )}
            {booking ? (
              <>
                <div className="mt-1.5 flex justify-between border-t border-dashed border-gray-300 pt-2 text-gray-800">
                  <dt>Order total</dt><dd className="tabular-nums">{formatINR(order.total)}</dd>
                </div>
                <div className="flex justify-between py-1 text-base font-bold text-gray-900">
                  <dt>Paid today</dt><dd className="tabular-nums">{formatINR(order.paidNow)}</dd>
                </div>
                <div className="flex justify-between py-1 text-gray-800">
                  <dt>Balance before dispatch</dt><dd className="tabular-nums">{formatINR(order.balance)}</dd>
                </div>
              </>
            ) : (
              <div className="mt-1.5 flex justify-between border-t border-dashed border-gray-300 pt-2 text-base font-bold text-gray-900">
                <dt>Paid</dt><dd className="tabular-nums">{formatINR(order.paidNow ?? order.total)}</dd>
              </div>
            )}
          </dl>
          {order.gstin && <p className="mt-2 text-[12px] text-gray-600">GST invoice to GSTIN {order.gstin}</p>}
        </section>

        <div className="flex gap-3">
          <a href={whatsapp} target="_blank" rel="noopener noreferrer"
            className="flex min-h-[48px] flex-1 items-center justify-center rounded-xl border-[1.5px] border-green-600 bg-white text-sm font-bold text-green-700">
            WhatsApp us
          </a>
          <a href={`tel:${BUSINESS.phone}`}
            className="flex min-h-[48px] flex-1 items-center justify-center rounded-xl border-[1.5px] border-gray-300 bg-white text-sm font-bold text-gray-900">
            Call us
          </a>
        </div>
        <Link to="/daikin-ac-price-list-delhi" className="block py-2 text-center text-sm font-medium text-gray-600">
          Continue shopping
        </Link>
      </main>
    </>
  );
}
